import { useEffect, useRef, useState } from "react";

import { useDispatch } from "react-redux";

import { deleteTask } from "../../store/tasks/slice";
import { apiErrorMessage } from "../../utils/apiError";
import { SuccessToast } from "../../utils/customToast";
import Icons from "../common/Icons";

export const TaskDelete = ({
  isOpen,
  selectedTaskId,
  setIsTaskModifyCancel,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // The dialog stays mounted across task identities and across open/close, so
  // a refusal for the PREVIOUS task would otherwise still be on screen when it
  // reopens for a different one.
  useEffect(() => {
    setError("");
  }, [selectedTaskId, isOpen]);

  // Marks the current OPENING of this dialog for one task. An in-flight delete
  // cannot be aborted (no AbortController reaches the thunk, and the row may
  // already be gone server-side), so the resolution path instead has to decide
  // whether its side effects are still wanted. The cleanup bumps this whenever
  // the dialog stops being the thing the student is looking at: closed,
  // retargeted at a different task, or unmounted because the parent drawer
  // closed. A resolution stamped with an older opening drops its side effects.
  const openingRef = useRef(0);
  useEffect(() => {
    return () => {
      openingRef.current += 1;
    };
  }, [selectedTaskId, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDeleting) return;

    // `.unwrap()` is load-bearing. Without it the dispatch resolves for a
    // REJECTED delete exactly as it does for a fulfilled one, and this handler
    // used to close both dialogs and toast "Task deleted" unconditionally: a
    // refused delete reported success and took the task off the student's
    // screen while it was still very much on the server.
    const opening = openingRef.current;
    setIsDeleting(true);
    try {
      await dispatch(deleteTask(selectedTaskId)).unwrap();
      // The student pressed CANCEL or the close control while this was in
      // flight, or the parent drawer went away. Rendering null reads to them
      // as "backed out", so the dialog they dismissed must not then close
      // their editing drawer and announce a deletion they think they undid.
      if (opening !== openingRef.current) return;
      setIsTaskModifyCancel();
      onClose();
      SuccessToast("Task deleted");
    } catch (err) {
      // Stay open, keep the task rendered, say what actually happened. Same
      // staleness rule: a dialog the student already dismissed says nothing.
      if (opening !== openingRef.current) return;
      setError(
        apiErrorMessage(
          err,
          "This task could not be deleted. Nothing changed.",
        ),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="font-[Afacad Flux] fixed inset-0 bg-darkblue bg-opacity-65 flex justify-center items-center">
      <div className="bg-background-blackpearl shadow-lg w-3/5 2xl:w-1/2">
        <div className="flex justify-end items-center pl-6 pr-3 pt-3 pb-4 mb-20">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="text-[#F5EFF7] text-2xl justify-end disabled:opacity-50 disabled:cursor-not-allowed"
          >
            &times;
          </button>
        </div>
        <div className="p-6 mb-20">
          <div className="flex justify-center mb-4">
            <Icons type="warning" className="text-persimmon" />
          </div>
          <p className="mb-4 text-[24px] font-normal leading-[32px] uppercase text-center text-[#F4F6FB]">
            ARE YOU SURE YOU WANT TO{" "}
            <span className="text-persimmon">DELETE</span>?
          </p>
          <p className="text-[11px] font-normal leading-[20px] lowercase text-center text-[#F4F6FB]">
            All the details of the task will be lost if you delete this.
          </p>
          {error && (
            <p
              role="alert"
              className="mt-4 text-[13px] font-normal leading-[20px] text-center text-persimmon"
            >
              {error}
            </p>
          )}
        </div>
        <form className="flex justify-between p-6" onSubmit={handleSubmit}>
          <button
            type="submit"
            disabled={isDeleting}
            className="flex items-center gap-2 bg-persimmon text-black font-medium py-2 px-4 rounded-full text-base leading-5 tracking-wide uppercase border-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icons type="trash" color="black" />
            DELETE
          </button>
          <button
            type="button"
            onClick={() => {
              onClose();
            }}
            disabled={isDeleting}
            className="text-persimmon font-medium py-2 px-4 rounded-full text-base leading-5 tracking-wide uppercase disabled:opacity-50 disabled:cursor-not-allowed"
          >
            CANCEL
          </button>
        </form>
      </div>
    </div>
  );
};
